import empty from "is-empty";
import moment from "moment-timezone";
import { createSelector } from "@reduxjs/toolkit";

import { FETCH_STATES, TX_STATES, LOAN_TYPES } from "./constants";
import { ROLE } from "src/redux/TopBar/constants";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import * as Validators from "src/utils/validators";
import CurrencyConverter from "src/utils/CurrencyConversionService";
import { InventorySelectors } from "src/redux/Inventory/selectors";
import { getTermFromDate, qtyByCountry, getVWAvgRateFromBps } from "src/utils";
import {
    toUiCollType,
    convertCollateral,
    convertRate,
} from "src/utils/converting";
import { pickValidNumber } from "src/utils/number";
import { ERROR_TYPE as SPLIT_OFFER_ERROR_TYPES } from "src/components/common/AgGrid/TruTooltip/constants";

const mapTxEntryToRow = (entry, { inventory, isPartnerCpty }) => {
    // Extract frequently used objects with shorter names
    const {
        security: sec,
        requestedEconomics: re,
        offeredEconomics: oe,
        originOfferedEconomics: ooe,
        originRequestedEconomics: ore,
    } = entry;

    const baseRow = {
        id: entry.id,
        idx: entry.idx,
        needId: entry.needId,
        LastUpdated: entry.lastUpdated,
        CreatedAt: new Date(entry.createdAt * 1000),
        ListName: entry.listName,
        FundName: entry.fundName,
        Status: entry.state,
        StatusName: Validators.translateStatus(entry.state),
        prevStatus: entry.prevState,
        Comment: entry.comment,
        Response: entry.Response,
        PrevListName: entry.prevListName ?? entry.listName,
        User: entry.lastUser,
        Type: entry.type,
        TxType: entry.TxType,
        MyRole: entry.myRole,
        InternalFundCode: entry.internalFundCode,
        isEAActionable:
            (entry.state === TX_STATES.CONFIRMED ||
                entry.state === TX_STATES.FORKED ||
                entry.state === TX_STATES.UPGRADE_REQUESTED) &&
            entry.type === LOAN_TYPES.HOLD,
        OrderId: entry.orderId,
        NewType: entry.newType ?? entry.type,
        OriginType: entry.originType ?? entry.type,
        Objective: entry.objective,
        OriginObjective: entry.originObjective ?? entry.objective,
        unsolicitedOffer: entry.unsolicitedOffer,
        Purpose: entry.purpose,
        FR: entry.fr,
        isPortfolioTrade: entry.portfolioTrade,
        isRemovedConstituent: entry.removedConstituent,
    };

    const inventoryData = inventory
        ? {
              UploadedQuantity: inventory.uploadedQuantity,
              PreferredRate: inventory.preferredRate,
              Utilization: inventory.utilization,
              RemainingQuantity: inventory.truQuantity,
              PreferredVD: inventory.valueDate,
          }
        : {};

    const originValue = {
        ...(ooe
            ? {
                  OriginOfferRate: ooe.fee?.rateBp
                      ? ooe.fee.rateBp / 100.0
                      : null,
                  OriginOfferQuantity: ooe.quantity,
                  OriginOfferedMaturity: ooe.maturityDate,
              }
            : {}),

        ...(ore
            ? {
                  IncQuantity: ore.incQuantity,
                  MinQuantity: ore.minQuantity,
                  MaxOffers: re?.maxOffers,
                  Callable: ore.callable,
                  ValueDate: ore.valueDate,
                  Maturity: ore.maturityDate,
                  TxType: ore.txType,
                  Intermediary: ore.intermediary,
                  Dividend: ore.dividend,
                  DividendIndicator:
                      ore.dividendIndicator ?? re?.dividendIndicator,
                  OriginRequestRate: re ? convertRate(re) : undefined,
                  ...(ore.collateralType
                      ? (() => {
                            const { collType, collCcy } = toUiCollType(
                                ore.collateralType
                            );
                            return {
                                CollateralType: collType,
                                CollateralCCY: collCcy,
                            };
                        })()
                      : {}),
              }
            : {}),
    };

    const expiryData = (() => {
        const isExpiryState =
            entry.state === TX_STATES.PRE_LOAN_REQUESTED ||
            entry.state === TX_STATES.OFFERED ||
            entry.state === TX_STATES.FORKED ||
            (entry.type === LOAN_TYPES.HOLD &&
                entry.state === TX_STATES.CONFIRMED);

        if (entry.expiresAt && isExpiryState) {
            return { ExpiresAt: entry.expiresAt };
        } else if (
            entry.eaUpgradeRequestExpireAt &&
            entry.state === TX_STATES.UPGRADE_REQUESTED
        ) {
            return { ExpiresAt: entry.eaUpgradeRequestExpireAt };
        }
        return {};
    })();

    const cptyData = {
        ...(entry.lender && { Lender: entry.lender.name }),
        ...(entry.borrower && { Borrower: entry.borrower.name }),
    };

    const securityData = sec
        ? (() => {
              const country = sec.country ?? "KR";
              return {
                  SecurityId: sec.id,
                  ISIN: sec.isin,
                  Name: sec.name,
                  LocalName: sec.localName,
                  BbgCode: sec.bbgCode,
                  Ticker: sec.ticker,
                  PreviousClose: CurrencyConverter.convertFromCountry(
                      sec.closingPrice,
                      country
                  ).value(),
                  Country: country,
                  TradeableDates: sec.tradableDates?.map((date) =>
                      date.replace(/-/g, "")
                  ),
                  security: sec,
              };
          })()
        : {};

    const requestedData = re
        ? (() => {
              const data = {
                  ReqQuantity: re.quantity,
                  ...(entry.myRole === "LENDER" && {
                      IsPartnerTrade: isPartnerCpty,
                  }),
                  IncQuantity: re.incQuantity,
                  MinQuantity: re.minQuantity,
                  MaxOffers: re.maxOffers,
                  Callable: re.callable,
                  ReqRate: convertRate(re),
                  Dividend: re.dividend,
                  TxType: re.txType,
                  Intermediary: re.intermediary,
              };

              if (re.valueDate) {
                  data.ValueDate = re.valueDate;
              }

              if (re.maturityDate) {
                  data.Maturity = re.maturityDate;
                  data.Term = getTermFromDate(data.ValueDate, re.maturityDate);
              }

              if (sec && Validators.isAmountValid(re.quantity, sec.country)) {
                  data.ReqAmount = CurrencyConverter.convertFromCountry(
                      sec.closingPrice * re.quantity,
                      sec.country
                  ).value();
              }

              return data;
          })()
        : {};

    const offeredData = oe
        ? (() => {
              const data = {
                  OfferQuantity: oe.quantity,
                  Callable: oe.callable,
                  Dividend: oe.dividend,
                  OfferRate: convertRate(oe),
              };

              if (oe.valueDate) {
                  data.ValueDate = oe.valueDate;
              }

              if (oe.txType) data.TxType = oe.txType;
              if (oe.intermediary) data.Intermediary = oe.intermediary;

              if (sec && Validators.isAmountValid(oe.quantity, sec.country)) {
                  data.OfferAmount = CurrencyConverter.convertFromCountry(
                      sec.closingPrice * oe.quantity,
                      sec.country
                  ).value();
              }

              if (oe.maturityDate || entry.state === TX_STATES.OFFERED) {
                  const isDateValid = moment(oe.maturityDate).isValid();
                  data.Maturity = isDateValid ? oe.maturityDate : null;
                  data.Term = isDateValid
                      ? getTermFromDate(data.ValueDate, data.Maturity)
                      : "";
              }

              return data;
          })()
        : {};

    const { CollateralCCY, CollateralType, SelectableCCY } =
        convertCollateral(entry);
    const collateralData = { CollateralCCY, CollateralType, SelectableCCY };

    return {
        ...baseRow,
        ...inventoryData,
        ...expiryData,
        ...cptyData,
        ...securityData,
        ...requestedData,
        ...offeredData,
        ...collateralData,
        OriginValue: originValue,
    };
};

const txInitIsReadyReduce = (role) => (isValid, row) =>
    isValid &&
    Validators.isTxInitRowValid(row, role) &&
    Validators.isMarketDataFetched(row.marketData, row.isRemoved);

const selectRole = (state) => TopBarSelectors.Role(state);
const selectTxBorrowEntries = (state) => state.Tx.txBorrowEntries;
const selectTxLendEntries = (state) => state.Tx.txLendEntries;
const selectSelectedRowIds = (state) => state.Tx.selectedRowIds || [];
const selectFeatureFlags = (state) => SettingsSelectors.FeatureFlags(state);

const selectTxEntryMetadata = (state, entry) => {
    const inventory = InventorySelectors.GetInventory(
        state,
        entry.security?.ticker,
        entry.listName
    );
    const isPartnerCpty =
        entry.myRole === "LENDER"
            ? CounterPartysSelectors.IsPartnerCpty(state, entry.borrower?.id)
            : false;

    return { inventory, isPartnerCpty };
};

/*
 * memoized selectors
 */
const selectTxRawEntries = createSelector(
    [selectRole, selectTxBorrowEntries, selectTxLendEntries],
    (role, txBorrowEntries, txLendEntries) => {
        return role === ROLE.BORROWER ? txBorrowEntries : txLendEntries;
    }
);

const selectTxUiEntries = createSelector(
    [selectTxRawEntries, (state) => state],
    (entries, state) => {
        return entries.map((entry) => {
            const metadata = selectTxEntryMetadata(state, entry);
            return mapTxEntryToRow(entry, metadata);
        });
    }
);

const selectSelectedTxRawEntries = createSelector(
    [selectTxRawEntries, selectSelectedRowIds],
    (entries, selectedRowIds) => {
        if (selectedRowIds.length <= 0) return [];
        return entries
            .filter((e) => !e.isPortfolioTrade || !e.parentId)
            .filter((e) => selectedRowIds.includes(e.id));
    }
);

const selectSelectedTxUiEntries = createSelector(
    [selectTxUiEntries, selectSelectedRowIds],
    (entries, selectedRowIds) => {
        if (selectedRowIds.length <= 0) return [];
        return entries.filter((e) => selectedRowIds.includes(e.id));
    }
);

const selectTxEntriesToProceed = createSelector(
    [selectSelectedTxRawEntries, selectFeatureFlags, (state) => state],
    (selectedTxRawEntries, featureFlags, state) => {
        if (selectedTxRawEntries.length <= 0) return [];

        return selectedTxRawEntries.filter((e) =>
            Validators.isTxDataRowValid(e, {
                enabledMinIncrementQty:
                    featureFlags["enable-min-increment-quantity"],
                enabled90DayMaxMaturity:
                    featureFlags["enable-90-day-max-maturity-date"],
                // Spec §2.1.3.2 / §2.1.3.3 — when limited-ui is on the
                // submit-button must agree with the cell-level (red)
                // and row-level (orange) validators about the lender's
                // qty/rate clamps. Thread the flag through to
                // isTxDataRowValid -> isTxQuantityValid / isTxRateValid.
                limitedUi: featureFlags["limited-ui"],
                selectedTxRows: selectedTxRawEntries,
                getIsPartnerTrade: (borrowerId) =>
                    CounterPartysSelectors.IsPartnerCpty(state, borrowerId),
                getTotalOfferForPreloan: (preloanId) =>
                    makeTotalOfferForPreloanId(preloanId)(state),
            })
        );
    }
);

const selectTxEntriesToAccept = createSelector(
    [selectTxEntriesToProceed, selectRole],
    (entries, role) => {
        return role === ROLE.LENDER
            ? entries.filter(
                  (entry) =>
                      (entry.state === TX_STATES.PRE_LOAN_REQUESTED &&
                          entries.filter((e) => e.preloanId === entry.id)
                              .length === 0) ||
                      entry.state === TX_STATES.OFFERING
              )
            : entries.filter((entry) => entry.state === TX_STATES.OFFERED);
    }
);

const selectAggregatedTxEntries = createSelector(
    [selectTxRawEntries, (state) => state],
    (entries, state) => {
        return entries.map((entry) => {
            const metadata = selectTxEntryMetadata(state, entry);
            let tx = mapTxEntryToRow(entry, metadata);

            const hasChildren =
                entries.some(
                    (e) => e.preloanId !== e.id && e.preloanId === entry.id
                ) ||
                (entry.portfolioTrade && !entry.parentId);
            if (hasChildren) {
                tx.isParent = true;
                return tx;
            }

            // splits of solicited offers
            const splitOfferParent = entries.find(
                (e) =>
                    !e.unsolicitedOffer &&
                    e.id !== e.preloanId &&
                    e.id === entry.preloanId
            );
            if (splitOfferParent) {
                const parentMetadata = selectTxEntryMetadata(
                    state,
                    splitOfferParent
                );
                tx.ParentId = entry.preloanId;
                tx.ParentData = mapTxEntryToRow(
                    splitOfferParent,
                    parentMetadata
                );
                return tx;
            }

            // constituents of portfolio trade
            if (entry.portfolioTrade && entry.parentId) {
                tx.ParentId = entry.parentId;
                return tx;
            }

            return tx;
        });
    }
);

const selectTxEntriesToReject = createSelector(
    [selectSelectedTxRawEntries, selectRole],
    (entries, role) => {
        return role === ROLE.LENDER
            ? entries.filter(
                  (entry) => entry.state === TX_STATES.PRE_LOAN_REQUESTED
              )
            : entries.filter((entry) => entry.state === TX_STATES.OFFERED);
    }
);

const selectIsTxHoldSelectable = createSelector(
    [
        (state, entry, role) => entry.type,
        (state, entry, role) => entry.state,
        (state, entry, role) => role,
        (state, entry, role) => state.Tx.selectedRowStatus,
    ],
    (type, status, role, selectedRowStatus) => {
        if (type !== LOAN_TYPES.HOLD) return false;

        // When no selection or selected rows is not in the same status
        if (selectedRowStatus !== null && status !== selectedRowStatus)
            return false;

        return (
            status === TX_STATES.CONFIRMED ||
            (role === "LENDER" && status === TX_STATES.UPGRADE_REQUESTED) ||
            (role === "BORROWER" && status === TX_STATES.FORKED)
        );
    }
);

const selectTxEntriesToFork = createSelector(
    [selectTxEntriesToProceed, selectRole, (state) => state],
    (entries, role, state) => {
        if (role !== ROLE.LENDER) return [];
        return entries.filter((entry) =>
            selectIsTxHoldSelectable(state, entry, "LENDER")
        );
    }
);

const selectTxEntriesToUpgrade = createSelector(
    [selectTxEntriesToProceed, selectRole, (state) => state],
    (entries, role, state) => {
        if (role !== ROLE.BORROWER) return [];
        return entries.filter((entry) =>
            selectIsTxHoldSelectable(state, entry, "BORROWER")
        );
    }
);

const selectTxEntriesToRelease = createSelector(
    [selectTxEntriesToProceed, selectRole, (state) => state],
    (entries, role, state) => {
        if (role !== ROLE.BORROWER) return [];
        return entries.filter((entry) =>
            selectIsTxHoldSelectable(state, entry, "BORROWER")
        );
    }
);

const selectTxEntriesToAcceptUpgrade = createSelector(
    [selectTxEntriesToProceed, selectRole, (state) => state],
    (entries, role, state) => {
        if (role !== ROLE.LENDER) return [];
        return entries.filter((entry) =>
            selectIsTxHoldSelectable(state, entry, "LENDER")
        );
    }
);

const selectTxEntriesToRejectUpgrade = createSelector(
    [selectSelectedTxRawEntries, selectRole, (state) => state],
    (entries, role, state) => {
        if (role !== ROLE.LENDER) return [];
        return entries.filter((entry) =>
            selectIsTxHoldSelectable(state, entry, "LENDER")
        );
    }
);

const selectTxHasEntriesToFork = createSelector(
    [selectTxEntriesToProceed],
    (entries) => {
        return entries.some(
            (entry) =>
                entry.type === LOAN_TYPES.HOLD &&
                entry.state === TX_STATES.CONFIRMED
        );
    }
);

const selectTxHasPartnerEntries = createSelector(
    [selectSelectedTxUiEntries],
    (selectedTxEntries) => {
        return selectedTxEntries.some(
            (entry) => entry.MyRole === "LENDER" && entry.IsPartnerTrade
        );
    }
);

// Port of oldest-sources/truweb-ui commit 31d59416 ("Restore strict
// row-validity check for the Approve button"). New arch inherited the
// post-TRUEF-6683 `>=` relaxation that unblocked portfolio trades but
// silently allowed split offers with invalid sibling rows through. Old
// arch fixed it with a per-id `.every(...)` helper that strips
// portfolio constituents before the strictness check; new arch never
// got the fix until now.
//
// NB: raw-state portfolio constituents use `portfolioTrade === true &&
// parentId`. The `isPortfolioTrade` alias only exists on UI-mapped
// rows (mapTxEntryToRow); a filter on it against raw state is a
// no-op. `selectSelectedTxRawEntries` currently filters by
// `!e.isPortfolioTrade || !e.parentId` which is therefore a latent
// no-op — out of scope here, same as in the old-arch fix.
export const isSelectedRowsAcceptableInner = (
    entriesToAccept,
    selectedRowIds,
    rawEntries
) => {
    if (selectedRowIds.length === 0) return false;
    const constituentIds = new Set(
        rawEntries
            .filter((e) => e.portfolioTrade === true && e.parentId)
            .map((e) => e.id)
    );
    const nonConstituentSelectedIds = selectedRowIds.filter(
        (id) => !constituentIds.has(id)
    );
    if (nonConstituentSelectedIds.length === 0) return false;
    const acceptableIds = new Set(entriesToAccept.map((e) => e.id));
    return nonConstituentSelectedIds.every((id) => acceptableIds.has(id));
};

const selectIsSelectedRowsAcceptable = createSelector(
    [selectTxEntriesToAccept, selectSelectedRowIds, selectTxRawEntries],
    isSelectedRowsAcceptableInner
);

const selectIsSelectedEARowsAcceptable = createSelector(
    [
        selectRole,
        selectTxEntriesToUpgrade,
        selectTxEntriesToFork,
        selectSelectedRowIds,
    ],
    (role, entriesToUpgrade, entriesToFork, selectedRowIds) => {
        const entriesToAcceptLength =
            role === ROLE.BORROWER
                ? entriesToUpgrade.length
                : entriesToFork.length;
        return (
            entriesToAcceptLength > 0 &&
            selectedRowIds.length === entriesToAcceptLength
        );
    }
);

const selectIsSelectedEARowsReleasable = createSelector(
    [selectTxEntriesToRelease],
    (entriesToReject) => entriesToReject.length > 0
);

const selectIsSelectedEARowsUpgradeAcceptable = createSelector(
    [selectTxEntriesToAcceptUpgrade, selectSelectedRowIds],
    (entriesToAcceptUpgrade, selectedRowIds) => {
        return (
            entriesToAcceptUpgrade.length > 0 &&
            selectedRowIds.length === entriesToAcceptUpgrade.length
        );
    }
);

const selectIsSelectedEARowsUpgradeRejectable = createSelector(
    [selectTxEntriesToRejectUpgrade],
    (entriesToRejectUpgrade) => entriesToRejectUpgrade.length > 0
);

const selectIsSelectedRowsRejectable = createSelector(
    [selectTxEntriesToReject],
    (entriesToReject) => entriesToReject.length > 0
);

const selectEntriesCountries = createSelector(
    [selectTxEntriesToProceed],
    (entries) => {
        return new Set(entries.map((entry) => entry.security.country));
    }
);

const selectIsMultiMarketSelected = createSelector(
    [selectEntriesCountries],
    (countries) => countries.size > 1
);

const selectTxTotalValueToProceed = createSelector(
    [
        selectTxEntriesToProceed,
        (state) => SettingsSelectors.UserSettings(state)?.preferredCcy,
        selectIsMultiMarketSelected,
        selectEntriesCountries,
    ],
    (entries, preferredCcy, isMultiMarketSelected, countries) => {
        if (empty(entries) || (isMultiMarketSelected && !preferredCcy)) {
            return null;
        }

        if (!isMultiMarketSelected) {
            const country = entries[0].security.country;
            return CurrencyConverter.convertFromCountry(
                entries.reduce((total, entry) => {
                    const qty = entry.offeredEconomics.quantity;
                    return total + entry.security.closingPrice * parseInt(qty);
                }, 0),
                country
            ).value();
        }

        return entries.reduce((total, entry) => {
            const qty = entry.offeredEconomics.quantity;
            return (
                total +
                CurrencyConverter.convertFromCountry(
                    entry.security.closingPrice * parseInt(qty),
                    entry.security.country
                ).value()
            );
        }, 0);
    }
);

const selectTxVwavgToProceed = createSelector(
    [
        selectTxEntriesToProceed,
        (state) => SettingsSelectors.UserSettings(state)?.preferredCcy,
        selectIsMultiMarketSelected,
    ],
    (entries, preferredCcy, isMultiMarketSelected) => {
        if (empty(entries) || (isMultiMarketSelected && !preferredCcy)) {
            return null;
        }

        return {
            offer: getVWAvgRateFromBps(
                entries.map((entry) => ({
                    rate: pickValidNumber(
                        entry.offeredEconomics?.fee?.rateBp,
                        entry.offeredEconomics?.rateRebate?.rateBp
                    ),
                    value: CurrencyConverter.convertFromCountry(
                        entry.offeredEconomics?.quantity *
                            entry.security.closingPrice,
                        entry.security.country
                    ).value(),
                }))
            ),
            request: getVWAvgRateFromBps(
                entries.map((entry) => ({
                    rate: pickValidNumber(
                        entry.requestedEconomics?.fee?.rateBp,
                        entry.requestedEconomics?.rateRebate?.rateBp
                    ),
                    value: CurrencyConverter.convertFromCountry(
                        entry.requestedEconomics?.quantity *
                            entry.security.closingPrice,
                        entry.security.country
                    ).value(),
                }))
            ),
        };
    }
);

/*
 * selector factories
 */
const makeSelectedTxTotalOfferedQty = (listName, securityId) =>
    createSelector([selectSelectedTxRawEntries], (entries) => {
        return entries
            .filter(
                (e) => e.listName === listName && e.security?.id === securityId
            )
            .map((e) => e.offeredEconomics.quantity)
            .reduce((a, b) => a + b, 0);
    });

const makeTotalOfferForPreloanId = (preloanId) =>
    createSelector([selectTxRawEntries], (entries) => {
        return entries
            .filter(
                (e) =>
                    e.preloanId !== undefined &&
                    e.preloanId === preloanId &&
                    e.offeredEconomics.quantity !== undefined
            )
            .map((e) => e.offeredEconomics.quantity)
            .reduce((a, b) => a + b, 0);
    });

const makeTotalSplitOfferCountForPreloanId = (preloanId) =>
    createSelector([selectTxRawEntries], (entries) => {
        return entries
            .filter(
                (e) => e.preloanId !== undefined && e.preloanId === preloanId
            )
            .reduce((a, b) => a + 1, 0);
    });

const makeGetMaxOffersForId = (id) =>
    createSelector([selectTxRawEntries], (entries) => {
        return entries.find((e) => e.id === id)?.requestedEconomics?.maxOffers;
    });

const makeCountSelectedRowsForListAndSecurity = (listName, securityId) =>
    createSelector([selectSelectedTxRawEntries], (entries) => {
        return entries.filter(
            (e) => e.listName === listName && e.security?.id === securityId
        ).length;
    });

const makeGetChildEntries = (preloanId) =>
    createSelector([selectTxLendEntries], (txLendEntries) => {
        const childEntries = txLendEntries.filter(
            (e) => e.preloanId === preloanId
        );
        const parentEntry = txLendEntries.find((e) => e.id === preloanId);

        if (!parentEntry) return { index: -1 };

        const currIndex =
            childEntries.length > 0
                ? childEntries.map((c) => c.index).sort((a, b) => b - a)[0]
                : 0;

        return { parentEntry, childEntries, index: currIndex + 1 };
    });

const makeIsSplitOfferAllowed = (id) =>
    createSelector(
        [
            selectFeatureFlags,
            (state) => makeTotalSplitOfferCountForPreloanId(id)(state),
            (state) => makeGetMaxOffersForId(id)(state),
        ],
        (featureFlags, totalSplits, maxOffers) => {
            const { "enable-max-offers": enabledMaxOffers } = featureFlags;
            if (!enabledMaxOffers) return { isSplitAllowed: true };

            // Before the first split there will be zero split offers,
            // in this case we must count the selected offer as one
            // to prevent splitting the offer if MaxOffers is 0 or 1
            const totalOffers = totalSplits === 0 ? 1 : totalSplits;

            if (maxOffers !== undefined && totalOffers >= maxOffers) {
                return {
                    isSplitAllowed: false,
                    error:
                        totalOffers === 1
                            ? SPLIT_OFFER_ERROR_TYPES.SPLIT_NOT_ALLOWED
                            : SPLIT_OFFER_ERROR_TYPES.SPLIT_MAXED_OUT,
                };
            }

            return { isSplitAllowed: true };
        }
    );

export const TxSelectors = {
    /**
     * TX_LIST
     */
    HideInactive: (state) => state.Tx.hideInactive,
    TxLendEntries: selectTxLendEntries,
    TxBorrowEntries: selectTxBorrowEntries,
    SelectedSecurity: (state) => state.Tx.selectedSecurity,
    SelectedRowIds: (state) => state.Tx.selectedRowIds || [],
    hasActionableEA: (state) => state.Tx.hasActionableEA,
    SelectedRowStatus: (state) => state.Tx.selectedRowStatus,
    TxRespondComment: (state) => state.Tx.respond.comment,
    TxRespondStatus: (state) => state.Tx.respond.status,

    TxRawEntries: selectTxRawEntries,
    TxEntries: selectTxUiEntries,
    AggregatedTxEntries: selectAggregatedTxEntries,
    TxEntriesToAccept: selectTxEntriesToAccept,
    TxEntriesToReject: selectTxEntriesToReject,
    TxEntriesToFork: selectTxEntriesToFork,
    TxEntriesToUpgrade: selectTxEntriesToUpgrade,
    TxEntriesToRelease: selectTxEntriesToRelease,
    TxEntriesToAcceptUpgrade: selectTxEntriesToAcceptUpgrade,
    TxEntriesToRejectUpgrade: selectTxEntriesToRejectUpgrade,
    TxHasEntriesToFork: selectTxHasEntriesToFork,
    isSelectedRowsAcceptable: selectIsSelectedRowsAcceptable,
    isSelectedEARowsAcceptable: selectIsSelectedEARowsAcceptable,
    isSelectedEARowsRelesable: selectIsSelectedEARowsReleasable,
    isSelectedEARowsUpgradeAcceptable: selectIsSelectedEARowsUpgradeAcceptable,
    isSelectedEARowsUpgradeRejectable: selectIsSelectedEARowsUpgradeRejectable,
    isSelectedRowsRejectable: selectIsSelectedRowsRejectable,
    TxTotalValueToProceed: selectTxTotalValueToProceed,
    TxVwavgToProceed: selectTxVwavgToProceed,
    TxHasSelectedPartnerEntries: selectTxHasPartnerEntries,

    isTxHoldSelectable: (state, entry, role) =>
        selectIsTxHoldSelectable(state, entry, role),

    SelectedTxTotalOfferedQty: (state, listName, securityId) =>
        makeSelectedTxTotalOfferedQty(listName, securityId)(state),

    TotalOfferForPreloanId: (state, preloanId) =>
        makeTotalOfferForPreloanId(preloanId)(state),

    GetMaxOffersForId: (state, id) => makeGetMaxOffersForId(id)(state),

    CountOfListSecuritySelectedRows: (state, listName, securityId) =>
        makeCountSelectedRowsForListAndSecurity(listName, securityId)(state),

    GetChildEntries: (state, preloanId) =>
        makeGetChildEntries(preloanId)(state),

    IsSplitOfferAllowed: (state, id) => makeIsSplitOfferAllowed(id)(state),

    /**
     * TX_INITIATE
     */
    TxInitState: (state) => state.Tx.initiate.status,
    TxInitErrors: (state) => state.Tx.initiate.errors,
    TxInitDisables: (state) => state.Tx.initiate.disables,
    TxInitStateError: (state) => state.Tx.initiate.errorMessage,
    TxInitSendingState: (state) => state.Tx.initiate.status,
    TxInitOfferSettings: (state) => state.Tx.initiate.offerSettings,
    TxInitOfferComment: (state) => state.Tx.initiate.comment,
    TxInitRequestSettings: (state) => state.Tx.initiate.requestSettings,
    HasMarkitAccess: (state) => state.Tx.initiate.hasMarkitAccess,
    TxIniCptyError: (state) => state.Tx.initiate.cptyError,
    TxCreateRowData: (state) => state.Tx.initiate.createRowData,

    TxInitRowData: createSelector(
        [(state) => state.Tx.initiate.createRowData, selectRole],
        (createRows, role) => {
            return createRows.map((row) => {
                const quantity = qtyByCountry(
                    row.Quantity,
                    row.Country,
                    role === ROLE.BORROWER
                );
                return {
                    ...row,
                    PreviousClose: CurrencyConverter.convertFromCountry(
                        row.security?.closingPrice,
                        row.Country
                    ).format(),
                    Amount:
                        row.marketData.status === FETCH_STATES.FETCHING
                            ? "" //Amount should be changed after fetching
                            : Validators.isAmountValid(
                                  row.Quantity,
                                  row.Country
                              )
                            ? CurrencyConverter.convertFromCountry(
                                  row.security?.closingPrice * quantity,
                                  row.Country
                              ).value()
                            : null,
                };
            });
        }
    ),

    CreateRowDataTxTotalOfferedQty: (state, listName, securityId) => {
        let totalOffered = state.Tx.initiate.createRowData
            .filter(
                (row) =>
                    !empty(row.ListName) &&
                    row.ListName === listName &&
                    row.SecurityId === securityId
            )
            .map((row) => row.Quantity)
            .reduce((a, b) => a + b, 0);
        return totalOffered;
    },

    CountOfListSecurityCreateRows: (state, listName, securityId) => {
        return state.Tx.initiate.createRowData.filter(
            (row) =>
                !empty(row.ListName) &&
                row.ListName === listName &&
                row.SecurityId === securityId
        ).length;
    },

    TxInitSelectedCptyIds: (state) => state.Tx.initiate.selectedCptys,
    TxInitSelectedCptys: createSelector(
        [(state) => state.Tx.initiate.selectedCptys, (state) => state],
        (selectedCptyIds, state) =>
            selectedCptyIds.map((i) => CounterPartysSelectors.Get(state, i))
    ),
    TxInitSelectedLoanType: (state) =>
        state.Tx.initiate.requestSettings.loanType,

    TxInitIsReady: (state) => {
        const role = TopBarSelectors.Role(state);
        if (role === ROLE.LENDER) {
            if (state.Tx.initiate.createRowData.length === 0) return false;

            if (
                state.Tx.initiate.createRowData.reduce(
                    txInitIsReadyReduce(role),
                    true
                ) &&
                state.Tx.initiate.errors.length === 0 &&
                !state.Table.isEditingCell
            ) {
                // check if sum of offers exceed inventory quantity
                var invQtyMap = new Map();
                const inventoryList =
                    InventorySelectors.GetInventoryList(state);
                inventoryList
                    .filter(
                        (inv) =>
                            !empty(inv.ListName) &&
                            Validators.isDynamicOrClientPoolList(inv.ListName)
                    )
                    .map((inv) => {
                        let key = inv.ISIN + inv.ListName;
                        return invQtyMap.set(
                            key,
                            invQtyMap.get(key) + inv.TruQuantity ||
                                inv.TruQuantity
                        );
                    });

                var offerQtyMap = new Map();
                state.Tx.initiate.createRowData
                    .filter(
                        (row) =>
                            !empty(row.ListName) &&
                            Validators.isDynamicOrClientPoolList(row.ListName)
                    )
                    .map((row) => {
                        // note: SecurityId here is actually ISIN
                        let key = row.SecurityId + row.ListName;
                        return offerQtyMap.set(
                            key,
                            offerQtyMap.get(key) + row.Quantity || row.Quantity
                        );
                    });

                for (const [key, offerQty] of offerQtyMap.entries()) {
                    if (invQtyMap.has(key) && offerQty > invQtyMap.get(key))
                        return false;
                }

                return true;
            }

            return false;
        } else {
            // Check if there are parent rows, and if so, ensure at least one child is not removed
            const hasParentRows = state.Tx.initiate.createRowData.some(
                (row) => row.parentId === null
            );
            const hasActiveChild =
                !hasParentRows ||
                state.Tx.initiate.createRowData.some(
                    (row) => row.parentId !== null && !row.isRemoved
                );

            return (
                state.Tx.initiate.createRowData.length &&
                hasActiveChild &&
                state.Tx.initiate.createRowData.reduce(
                    txInitIsReadyReduce(role),
                    true
                ) &&
                state.Tx.initiate.errors.length === 0 &&
                !state.Tx.initiate.requestSettings.error &&
                !state.Table.isEditingCell
            );
        }
    },
};
